import { create } from 'zustand'

type UiState = {
  // Collapse states
  isExamSectionOpen: boolean
  isTimepointSectionOpen: boolean
  isSourceTasksSectionOpen: boolean
  isDestinationTasksSectionOpen: boolean

  // Table expansion states
  expandedSubjectIds: Set<string>

  // Timepoint selection state
  selectedTempTimepointSubmissionId: string | null
  selectedTempTimepointSubmissionPseudoId: string | null
  selectedTempSubjectPseudoId: string | null

  // Toggle actions
  toggleExamSection: () => void
  toggleTimepointSection: () => void
  toggleSourceTasksSection: () => void
  toggleDestinationTasksSection: () => void
  toggleExpandedSubject: (subjectId: string) => void

  // Selection actions
  setTempSelectedTimepointSubmission: (
    timepointSubmissionId: string | null,
    timepointSubmissionPseudoId: string | null,
    subjectPseudoId: string | null,
  ) => void
  clearTempSelectedTimepointSubmission: () => void

  // Reset function
  resetUIState: () => void
}

export const useUiStore = create<UiState>((set) => ({
  // Default states - all expanded
  isExamSectionOpen: true,
  isTimepointSectionOpen: true,
  isSourceTasksSectionOpen: true,
  isDestinationTasksSectionOpen: true,

  // Default table expansion state - empty set
  expandedSubjectIds: new Set(),

  // Default timepoint selection state
  selectedTempTimepointSubmissionId: null,
  selectedTempTimepointSubmissionPseudoId: null,
  selectedTempSubjectPseudoId: null,

  // Toggle functions
  toggleExamSection: () => set((state) => ({ isExamSectionOpen: !state.isExamSectionOpen })),
  toggleTimepointSection: () => set((state) => ({ isTimepointSectionOpen: !state.isTimepointSectionOpen })),
  toggleSourceTasksSection: () => set((state) => ({ isSourceTasksSectionOpen: !state.isSourceTasksSectionOpen })),
  toggleDestinationTasksSection: () =>
    set((state) => ({
      isDestinationTasksSectionOpen: !state.isDestinationTasksSectionOpen,
    })),

  // Toggle subject expansion
  toggleExpandedSubject: (subjectId) =>
    set((state) => {
      const newExpandedSubjectIds = new Set(state.expandedSubjectIds)
      if (newExpandedSubjectIds.has(subjectId)) {
        newExpandedSubjectIds.delete(subjectId)
      } else {
        newExpandedSubjectIds.add(subjectId)
      }
      return { expandedSubjectIds: newExpandedSubjectIds }
    }),

  // Timepoint selection functions
  setTempSelectedTimepointSubmission: (timepointSubmissionId, timepointSubmissionPseudoId, subjectPseudoId) =>
    set({
      selectedTempTimepointSubmissionId: timepointSubmissionId,
      selectedTempTimepointSubmissionPseudoId: timepointSubmissionPseudoId,
      selectedTempSubjectPseudoId: subjectPseudoId,
    }),

  clearTempSelectedTimepointSubmission: () =>
    set({
      selectedTempTimepointSubmissionId: null,
      selectedTempTimepointSubmissionPseudoId: null,
      selectedTempSubjectPseudoId: null,
    }),

  // Reset function to set all to default values
  resetUIState: () =>
    set({
      isExamSectionOpen: true,
      isTimepointSectionOpen: true,
      isSourceTasksSectionOpen: true,
      isDestinationTasksSectionOpen: true,
      expandedSubjectIds: new Set(),
      selectedTempTimepointSubmissionId: null,
      selectedTempTimepointSubmissionPseudoId: null,
      selectedTempSubjectPseudoId: null,
    }),
}))
